import React, { ReactNode, useEffect, useState } from "react"

import { Remove } from "@mui/icons-material"
import { Box, IconButton, MenuItem, Select, TableRow, TextField, Tooltip } from "@mui/material"
import {
    GetTableRowId,
    HandleChange,
    HandleDeleteRow,
    HandlePaste,
    HandleSelect,
    MeasureConversion,
    TableColumn,
    TableRowType,
    TableVariant,
} from "../../models/Table"
import { StringUtils } from "../../utils/StringUtils"
import { isRowInserted } from "../../utils/TableUtils"
import { TimeUtils } from "../../utils/TimeUtils"
import { convertLengthUnits } from "../../utils/UnitCalculations"
import { useLocalStorageStore } from "../../zstore/LocalStorageStore"
import { NexAutocomplete } from "../Fields/Autocomplete"
import { NexCheckbox } from "../Fields/Checkbox"
import { NexTableCell, NexTableOverlayCell } from "./NexTableCell"
import { useNexTableStyles } from "./NexTableStyles"

interface TableRowProps<T> {
    row: T & TableRowType
    stickyColumns?: TableColumn<T>[]
    columns: TableColumn<T>[]
    handleDeleteRow: HandleDeleteRow
    handleChange: HandleChange<any, T>
    handlePaste: HandlePaste<T>
    handleSelect: HandleSelect
    getRowId: GetTableRowId<T>
    selected: boolean
    rowOverlay?: (row: T) => ReactNode
    canSelectRow?: (row: T & TableRowType) => boolean
}

export const NexTableRow = <T,>({
    row,
    stickyColumns,
    columns,
    getRowId,
    selected,
    rowOverlay,
    canSelectRow,
    handleSelect,
    ...props
}: TableRowProps<T>) => {
    const rowId = getRowId(row)
    const [showOverlay, setShowOverlay] = useState(false)

    const show = () => setShowOverlay(true)
    const hide = () => setShowOverlay(false)

    const select = (
        <NexTableCell>
            <NexCheckbox
                disabled={canSelectRow?.(row) === false}
                checked={selected}
                onClick={(e) => handleSelect(e.shiftKey, rowId)}
            />
        </NexTableCell>
    )

    // TODO Ideally we should use a popover or something else to display the overlay without having a bogus column
    //  to the right
    return (
        <>
            <TableRow
                selected={selected}
                sx={{ overflow: "hidden" }}
                //Show edit overlay
                onMouseEnter={show}
                onTouchStart={show}
                //Hide edit overlay
                onMouseLeave={hide}
                onTouchEnd={hide}
            >
                {stickyColumns && (
                    <NexTableCell sticky selected={selected}>
                        {select}
                        {stickyColumns.map((column, index) => (
                            <NexTableCell key={index} column={column}>
                                <TableCellVariant row={row} column={column} rowId={rowId} {...props} />
                            </NexTableCell>
                        ))}
                    </NexTableCell>
                )}
                {!stickyColumns && <>{select}</>}
                {columns.map((column, index) => {
                    return (
                        <NexTableCell column={column} key={index}>
                            <TableCellVariant row={row} column={column} rowId={rowId} {...props} />
                        </NexTableCell>
                    )
                })}
                {rowOverlay && <NexTableOverlayCell showOverlay={showOverlay}>{rowOverlay(row)}</NexTableOverlayCell>}
            </TableRow>
        </>
    )
}

const TableCellVariant = <R,>({
    column,
    row,
    rowId,
    handleChange,
    handleDeleteRow,
    handlePaste,
}: {
    column: TableColumn<R>
    row: R & TableRowType
    rowId: number
    handleChange: HandleChange<any, R>
    handleDeleteRow: HandleDeleteRow
    handlePaste: HandlePaste<R>
}) => {
    const { classes } = useNexTableStyles()
    const prefDistUnit = useLocalStorageStore((state) => state.preferredDistanceUnit)
    const prefHeightUnit = useLocalStorageStore((state) => state.preferredHeightUnit)
    const value = row[column.id]
    const error = !!(column.required && !value) || !!column?.cellError?.(rowId)
    const [inputValue, setInputValue] = useState<(R & TableRowType)[keyof R] | string>("")

    useEffect(() => {
        setInputValue(value)
    }, [value])

    const convert = <T,>(value: T, col: TableColumn<R>) => {
        const val = String(value ?? "")
        if (col.convertType === MeasureConversion.DISTANCE) {
            return convertLengthUnits(+val, prefDistUnit)
        }
        if (col.convertType === MeasureConversion.HEIGHT) {
            return convertLengthUnits(+val, prefHeightUnit)
        }
        return val
    }

    if (isRowInserted(rowId) && handleDeleteRow && column.primary) {
        return (
            <IconButton size="small" onClick={() => handleDeleteRow(rowId)}>
                <Remove fontSize="small" />
            </IconButton>
        )
    }

    if (column.renderCell) {
        return <Box>{column.renderCell(rowId, value as R)}</Box>
    }

    switch (column.variant) {
        case TableVariant.input:
            return (
                <TextField
                    error={error}
                    onPaste={(e: React.ClipboardEvent<HTMLDivElement>) => handlePaste(e, rowId, column.id)}
                    value={inputValue ?? ""}
                    size="small"
                    variant="outlined"
                    sx={{
                        minWidth: column.minWidth,
                        width: column.width,
                        maxWidth: column.maxWidth,
                        textAlign: "left",
                    }}
                    onBlur={() => {
                        const val = StringUtils.stringOrEmpty(inputValue).trim()
                        handleChange(val ?? null, column.id, rowId)
                    }}
                    onChange={(e) => setInputValue(e.target.value)}
                    InputProps={{ classes: { root: classes.table__input_root, input: classes.table__input } }}
                />
            )
        case TableVariant.select:
            return (
                <Select
                    error={error}
                    value={StringUtils.stringOrEmpty(value)}
                    disabled={row.disableRow}
                    onChange={(e) => handleChange(e.target.value, column.id, rowId)}
                    variant="outlined"
                    fullWidth
                    size="small"
                    MenuProps={{ sx: { maxHeight: 500 } }}
                >
                    {column.allowNoSelection === true && (
                        <MenuItem value={""} disabled={column.disableOptions?.(row).includes("None")}>
                            <em>None</em>
                        </MenuItem>
                    )}
                    {column.options.map((option, idx) => (
                        <MenuItem key={idx} value={option} disabled={column.disableOptions?.(row).includes(option)}>
                            {option}
                        </MenuItem>
                    ))}
                </Select>
            )
        case TableVariant.autocomplete: {
            const val = String(value ?? "")
            return (
                <NexAutocomplete
                    value={val}
                    options={column.options}
                    freeSolo
                    size="small"
                    onChange={(e, value) => handleChange(value, column.id, rowId)}
                    classes={{ inputRoot: classes.cell__autocomplete_padding }}
                />
            )
        }
        case TableVariant.textTooltip: {
            const text = value == null ? "" : typeof value === "string" ? value : JSON.stringify(value)

            return (
                <Tooltip title={text} disableFocusListener enterTouchDelay={0} leaveTouchDelay={5000}>
                    <Box maxWidth={200} overflow={"hidden"} textOverflow={"ellipsis"}>
                        {text}
                    </Box>
                </Tooltip>
            )
        }
        case TableVariant.time: {
            const time = typeof value === "string" ? value : undefined
            return <Box>{TimeUtils.formatTime(time)}</Box>
        }
        default:
            return <Box>{convert(value, column)}</Box>
    }
}
